// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import Badge from "@insite/mobius/Badge";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@plumberssupply/App/Components/XngCarousel/XngCarousel";
import { SwiperInstance } from "@plumberssupply/App/Components/XngCarousel/XngCarousel.models";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import ProductPrimaryImage, {
    ProductPrimaryImageStyles,
} from "@plumberssupply/Overrides/Components/ProductPrimaryImage";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: ProductDetailsPrimaryImageStyles;
}

type Props = OwnProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    swiperStyles?: SwiperStyle;
    primaryImageInCarousel?: ProductPrimaryImageStyles;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: 858px;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                    max-width: 100%;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                height: auto;
                max-height: 100%;
                max-width: 100%;
                object-fit: contain;
            `,
        },
        centeringWrapper: {
            css: css`
                margin: auto;
                max-height: 70vh;

                ${xScreen("gt", "xs")} {
                    border: 1px solid ${color("common.ui10")};
                }
            `,
        },
        container: {
            css: css`
                width: auto;
                ${xScreen("gt", "xs")} {
                    background: ${color("common.ui10")};
                }
            `,
        },
    },
    primaryImage: {
        container: {
            css: css`
                width: 308px;
                height: 308px;

                ${xScreen("lte", "md")} {
                    width: 328px;
                    height: 328px;
                }
                @media print {
                    width: 100px;
                    height: 100px;
                }
            `,
        },
        img: {
            css: css`
                margin: auto;
                width: auto;
                height: auto;
                max-height: 100%;
                max-width: 100%;
            `,
        },
    },
    primaryImageInCarousel: {
        img: {
            css: css`
                width: 308px;
                height: 308px;
                img {
                    width: 308px;
                    height: auto;
                }
                ${xScreen("lte", "md")} {
                    &&&& {
                        width: 328px;
                        height: 328px;
                        img {
                            width: 328px;
                        }
                    }
                }
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    // XNG-Changes
    swiperStyles: {
        container: {
            css: css`
                max-width: 328px;
                &&& {
                    .swiper-pagination-bullet {
                        margin: 20px 4px 0 4px;
                    }
                }
            `,
        },
    },
};

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    extendedStyles,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(primaryImageStyles, extendedStyles));

    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const [swiper, setSwiper] = React.useState<SwiperInstance>();

    // XNG-Changes
    const imageInCarouselClickHandler = (index: number) => {
        setSelectedImageIndex({ index });
        setFullImageModalIsOpen(true);
    };

    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    // XNG-CHANGES
    const { isSmallScreen } = useAppSelector(state => ({
        isSmallScreen: getXngCommon(state).isMobileScreen,
    }));
    React.useEffect(() => {
        if (isSmallScreen && (product?.images?.length || 0) > 1) {
            swiper?.slideTo(selectedImageIndex);
        }
    }, [selectedImageIndex]);

    return (
        <>
            {/* XNG-Changes become carousel for xs screens */}
            {isSmallScreen && (product?.images?.length || 0) > 1 ? (
                <XngCarousel
                    onSwiper={(swiper: SwiperInstance) => {
                        setSwiper(swiper);
                    }}
                    navigation={false}
                    slidesPerView={1}
                    extendedStyles={styles.swiperStyles}
                >
                    {product?.images?.map((image, index) => (
                        <ProductPrimaryImage
                            key={image.id}
                            product={product}
                            image={image}
                            onClick={() => imageInCarouselClickHandler(index)}
                            extendedStyles={styles.primaryImageInCarousel}
                        />
                    ))}
                </XngCarousel>
            ) : (
                <>
                    {!showOverlayBadge && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            onClick={imageClickHandler}
                            extendedStyles={styles.primaryImage}
                        />
                    )}
                    {showOverlayBadge && overlayBadge && (
                        <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                            <ProductPrimaryImage
                                product={product}
                                image={image}
                                onClick={imageClickHandler}
                                extendedStyles={styles.primaryImage}
                            />
                            <StyledWrapper
                                {...overlayBadgeWrapperStyles}
                                onClick={overlayBadgeWrapperClickHandler}
                                data-test-selector="Product-Details-Primary-Image-Badge"
                            >
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </>
            )}

            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={styles.fullPrimaryImage}
                />
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
